import { useState, useEffect, useCallback, useMemo } from "react"
import { useNavigate } from "react-router"
import AppLayout from "../../components/layout/AppLayout"
import Modal from "../../components/Modal"
import { colors, getAccountChartColor } from "../../lib/colors"
import PeriodSelectorModal from "../../components/analytics/PeriodSelectorModal"
import { readStoredPeriods } from "../../lib/dateRange"
import type { PeriodEntry } from "../../lib/dateRange"
import { api, type ApiError } from "../../lib/api"
import { useSelectedAccount } from "../../contexts/AccountContext"
import PlanLockNotice from "../../components/account/PlanLockNotice"
import AccountSelectionNotice from "../../components/account/AccountSelectionNotice"
import { PAGE_TITLES } from "../../lib/navItems"
import { downloadCsv, todayStr } from "../../lib/csv"
import type { CompetitorSummary } from "../../types/analytics"
import "../../styles/analytics.scss"

const STORAGE_KEY = "lyto.competitorsPeriod"

const CompetitorsPage = () => {
  const { selectedAccountIds, organizations, currentPermission, setSingleAccountOnly } = useSelectedAccount()
  const canEdit = currentPermission === "edit"
  const navigate = useNavigate()

  const accountColorMap = useMemo(() => {
    const map: Record<string, string> = {}
    selectedAccountIds.forEach((id, i) => {
      const account = organizations.flatMap(o => o.accounts).find(a => a.id === id)
      if (account) map[account.username] = getAccountChartColor(i).border
    })
    return map
  }, [selectedAccountIds, organizations])

  const selectedAccounts = useMemo(() =>
    organizations.flatMap(o => o.accounts).filter(a => selectedAccountIds.includes(a.id)),
    [selectedAccountIds, organizations]
  )

  const locked = selectedAccounts.length > 0 && selectedAccounts.some(a => !a.features.competitor)

  const [data, setData]           = useState<CompetitorSummary[]>([])
  const [loading, setLoading]     = useState(true)
  const [forbidden, setForbidden] = useState<string | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [username, setUsername]   = useState("")
  const [targetAccountId, setTargetAccountId] = useState<number | null>(null)
  const [adding, setAdding]       = useState(false)
  const [addError, setAddError]   = useState<string | null>(null)
  const [periods, setPeriods]     = useState<PeriodEntry[]>(() => readStoredPeriods(STORAGE_KEY))

  useEffect(() => {
    setSingleAccountOnly(periods.length > 1)
  }, [periods, setSingleAccountOnly])

  const fetchData = useCallback(async () => {
    if (selectedAccountIds.length === 0 || locked) { setLoading(false); return }
    setLoading(true)
    setForbidden(null)
    try {
      const p = periods[0]
      const params = new URLSearchParams({
        start_date: p.startDate,
        end_date:   p.endDate,
      })
      selectedAccountIds.forEach(id => params.append("instagram_account_ids[]", String(id)))
      const res = await api.get<CompetitorSummary[]>(`/analytics/competitors?${params}`)
      setData(res)
    } catch (e) {
      const err = e as ApiError
      if (err.status === 403) setForbidden(err.data?.error ?? `${PAGE_TITLES["/competitors"]}はプロプラン以上でご利用いただけます`)
    } finally {
      setLoading(false)
    }
  }, [selectedAccountIds, periods, locked])

  useEffect(() => { fetchData() }, [fetchData])

  const openModal = () => {
    setShowModal(true)
    setAddError(null)
    setUsername("")
    setTargetAccountId(selectedAccountIds[0] ?? null)
  }

  const handleAdd = async () => {
    if (!username.trim() || !targetAccountId) return
    setAdding(true)
    setAddError(null)
    try {
      await api.post(`/analytics/competitors`, {
        competitor: { username: username.trim() },
        instagram_account_id: targetAccountId,
      })
      setShowModal(false)
      setUsername("")
      fetchData()
    } catch (e: unknown) {
      const err = e as { data?: { error?: string } }
      setAddError(err.data?.error || "追加に失敗しました")
    } finally {
      setAdding(false)
    }
  }

  const handleDelete = async (competitor: CompetitorSummary, e: React.MouseEvent) => {
    e.stopPropagation()
    if (!confirm("この競合を削除しますか？")) return
    try {
      await api.delete(`/analytics/competitors/${competitor.id}?instagram_account_id=${competitor.instagram_account_id}`)
      setData(prev => prev.filter(c => c.id !== competitor.id))
    } catch {
    }
  }

  const fmtChange = (n: number) => {
    if (n > 0) return <span style={{ color: colors.success }}>+{n.toLocaleString()}</span>
    if (n < 0) return <span style={{ color: "#ef4444" }}>{n.toLocaleString()}</span>
    return <span style={{ color: colors.gray400 }}>±0</span>
  }

  const groupedByAccount = useMemo(() => {
    const map = new Map<string, CompetitorSummary[]>()
    data.forEach(c => {
      const key = c.account_username
      if (!map.has(key)) map.set(key, [])
      map.get(key)!.push(c)
    })
    return map
  }, [data])

  const renderTable = (competitors: CompetitorSummary[]) => {
    const handleCsvDownload = () => downloadCsv(
      [
        ["アカウント", "フォロワー数", "期間中増減", "投稿数", "いいね合計", "1投稿あたりいいね", "ENG率(%)"],
        ...competitors.map(c => [
          `@${c.username}`, c.followers_count, c.followers_change, c.posts_count,
          c.total_likes, c.avg_likes, c.avg_engagement_rate,
        ]),
      ],
      `competitors_${todayStr()}.csv`
    )
    return (
    <div className="posts-table">
      <div className="posts-table__header">
        <span className="posts-table__title">競合一覧</span>
        <button className="summary-table__csv-btn" onClick={handleCsvDownload}>↓ CSV</button>
      </div>
      <div className="posts-table__wrap">
        <table>
          <thead>
            <tr>
              <th>アカウント</th>
              <th>フォロワー数</th>
              <th>期間中増減</th>
              <th>投稿数</th>
              <th>いいね合計</th>
              <th>1投稿あたりいいね</th>
              <th>ENG率</th>
              <th>状態</th>
              {canEdit && <th></th>}
            </tr>
          </thead>
          <tbody>
            {competitors.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ color: colors.gray400, textAlign: "center", padding: "32px 0" }}>
                  競合が登録されていません
                </td>
              </tr>
            ) : (
              competitors.map(c => (
                <tr
                  key={c.id}
                  style={{ cursor: "pointer" }}
                  onClick={() => navigate(`/competitors/${c.id}?account_id=${c.instagram_account_id}`)}
                >
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {c.profile_picture_url ? (
                        <img
                          src={c.profile_picture_url}
                          alt=""
                          style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover" }}
                        />
                      ) : (
                        <div style={{ width: 32, height: 32, borderRadius: "50%", background: colors.gray200 }} />
                      )}
                      <span>@{c.username}</span>
                    </div>
                  </td>
                  <td>{c.followers_count.toLocaleString()}</td>
                  <td>{fmtChange(c.followers_change)}</td>
                  <td>{c.posts_count.toLocaleString()}</td>
                  <td>{c.total_likes.toLocaleString()}</td>
                  <td>{c.avg_likes != null ? c.avg_likes.toLocaleString() : "—"}</td>
                  <td>{c.avg_engagement_rate != null ? `${c.avg_engagement_rate}%` : "—"}</td>
                  <td>
                    {c.synced_at == null ? (
                      <span style={{ color: colors.gray400, fontSize: 12 }}>同期中...</span>
                    ) : (
                      <span style={{ color: colors.success, fontSize: 12 }}>同期済</span>
                    )}
                  </td>
                  {canEdit && (
                    <td>
                      <button
                        className="ps-btn"
                        style={{ color: "#ef4444", borderColor: "#ef4444", fontSize: 12 }}
                        onClick={(e) => handleDelete(c, e)}
                      >
                        削除
                      </button>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
  }

  return (
    <AppLayout pageTitle={PAGE_TITLES["/competitors"]}>
      <div className="analytics">
        {selectedAccountIds.length === 0 ? (
          <AccountSelectionNotice />
        ) : locked ? (
          <PlanLockNotice message={`${PAGE_TITLES["/competitors"]}はプロプラン以上でご利用いただけます`} />
        ) : forbidden ? (
          <PlanLockNotice message={forbidden} />
        ) : (
          <>
            <div className="analytics__header">
              {canEdit && (
                <button
                  className="ps-btn ps-btn--primary"
                  onClick={openModal}
                >
                  + 競合を追加
                </button>
              )}
              <PeriodSelectorModal
                  periods={periods}
                  onChange={setPeriods}
                  storageKey={STORAGE_KEY}
                />
            </div>

            {loading ? (
              <p style={{ color: colors.gray400 }}>読み込み中…</p>
            ) : selectedAccountIds.length === 1 ? (
              <div className="analytics__section">
                {renderTable(data)}
              </div>
            ) : (
              Array.from(groupedByAccount.entries()).map(([accountUsername, competitors]) => (
                <div key={accountUsername} className="analytics__section">
                  <div className="analytics__section-title">
                    <span
                      className="posts-table__username"
                      style={{ background: accountColorMap[accountUsername] ?? colors.gray400 }}
                    >
                      @{accountUsername}
                    </span>
                  </div>
                  {renderTable(competitors)}
                </div>
              ))
            )}
          </>
        )}
      </div>

      {showModal && (
        <Modal
          title="競合アカウントを追加"
          onClose={() => setShowModal(false)}
          footer={
            <>
              <button className="ps-btn" onClick={() => setShowModal(false)}>キャンセル</button>
              <button
                className="ps-btn ps-btn--primary"
                onClick={handleAdd}
                disabled={adding || !username.trim()}
              >
                {adding ? "追加中..." : "追加"}
              </button>
            </>
          }
        >
          {selectedAccounts.length > 1 && (
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", marginBottom: 8, fontSize: 14, fontWeight: 500 }}>
                対象アカウント
              </label>
              <select
                className="form-input"
                value={targetAccountId ?? ""}
                onChange={e => setTargetAccountId(parseInt(e.target.value))}
              >
                {selectedAccounts.map(a => (
                  <option key={a.id} value={a.id}>@{a.username}</option>
                ))}
              </select>
            </div>
          )}
          <label style={{ display: "block", marginBottom: 8, fontSize: 14, fontWeight: 500 }}>
            Instagramユーザー名
          </label>
          <input
            className="form-input"
            placeholder="@username"
            value={username}
            onChange={e => setUsername(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleAdd()}
            autoFocus
          />
          {addError && (
            <p style={{ color: "#ef4444", fontSize: 13, marginTop: 8 }}>{addError}</p>
          )}
        </Modal>
      )}
    </AppLayout>
  )
}

export default CompetitorsPage
